/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  DeductiblesResponse,
  GendersResponse,
  MaritalStatusResponse,
  OccupationsResponse,
  PreviousInsurancesResponse,
  TitlesResponse,
} from '../api/http/catalog.service';
import { MultiQuoteResponse, PrivateLiabilityDeductible } from '@core/models/swagger/bff';
import { EmailResponse, NumberResponse, PartnerSearchResponse } from '../api/http/validation.service';
import { DocumentResponse } from '../api/http/general.service';
import { InitFlow } from '@core/models/swagger';

@Injectable({
  providedIn: 'root',
})
export class Mocks {
  /**
   * Catalog Service
   */
  static getDeductibles(): Observable<DeductiblesResponse> {
    return of([
      {
        parameterId: PrivateLiabilityDeductible.None,
        parameterName: '0,00 €',
      },
      {
        parameterId: PrivateLiabilityDeductible._150,
        parameterName: '150,00 €',
      },
      {
        parameterId: PrivateLiabilityDeductible._300,
        parameterName: '300,00 €',
      },
      {
        parameterId: PrivateLiabilityDeductible._500,
        parameterName: '500,00 €',
      },
      {
        parameterId: PrivateLiabilityDeductible._1000,
        parameterName: '1.000,00 €',
      },
    ]);
  }

  static getGenders(): Observable<GendersResponse> {
    return of([
      {
        parameterId: 'Female',
        parameterName: 'Frau',
      },
      {
        parameterId: 'Male',
        parameterName: 'Herr',
      },
      {
        parameterId: 'None',
        parameterName: 'keine',
      },
    ]);
  }

  static getTitles(): Observable<TitlesResponse> {
    return of([
      {
        parameterId: 'NONE',
        parameterName: 'ohne',
      },
      {
        parameterId: 'DR',
        parameterName: 'Dr.',
      },
      {
        parameterId: 'DR_MED',
        parameterName: 'Dr. med',
      },
      {
        parameterId: 'PROF',
        parameterName: 'Prof.',
      },
      {
        parameterId: 'PROF_MED',
        parameterName: 'Prof. med',
      },
    ]);
  }

  static getOccupations(): Observable<OccupationsResponse> {
    return of([
      {
        parameterId: 'Employee',
        parameterName: 'Angestellt',
      },
      {
        parameterId: 'Worker',
        parameterName: 'Arbeitend',
      },
      {
        parameterId: 'NotWorking',
        parameterName: 'Ohne aktuelle Erwerbstätigkeit',
      },
      {
        parameterId: 'Official',
        parameterName: 'Verbeamtet',
      },
      {
        parameterId: 'Farmer',
        parameterName: 'Tätigkeit in der Landwirtschaft',
      },
      {
        parameterId: 'Student',
        parameterName: 'Studierend/Lernend',
      },
      {
        parameterId: 'ProfessionalSoldier',
        parameterName: 'BerufssoldatIn/Militärangehörig',
      },
      {
        parameterId: 'Pensioner',
        parameterName: 'Ruhestand',
      },
      {
        parameterId: 'SelfEmployed',
        parameterName: 'Selbständige-/Freiberufliche Tätigkeit',
      },
      {
        parameterId: 'HousePrivatier',
        parameterName: 'Haushaltsführende Person',
      },
    ]);
  }

  static getMaritalStatus(): Observable<MaritalStatusResponse> {
    return of([
      {
        parameterId: 'Married',
        parameterName: 'verheiratet',
      },
      {
        parameterId: 'Single',
        parameterName: 'ledig',
      },
      {
        parameterId: 'Divorced',
        parameterName: 'geschieden',
      },
      {
        parameterId: 'Widowed',
        parameterName: 'verwitwet',
      },
    ]);
  }

  static getClaimsAmount(): Observable<MaritalStatusResponse> {
    return of([
      {
        parameterId: 'None',
        parameterName: 'Keinen',
      },
      {
        parameterId: 'One',
        parameterName: '1',
      },
      {
        parameterId: 'Two',
        parameterName: '2',
      },
      {
        parameterId: '3orMore',
        parameterName: '3 oder mehr',
      },
    ]);
  }

  static getPreviousInsurances(): Observable<PreviousInsurancesResponse> {
    return of([
      {
        parameterId: '0501',
        parameterName: 'Öffentliche Sachversicherung Braunschweig',
      },
      {
        parameterId: '0502',
        parameterName: 'Öff.Vers. Bremen',
      },
      {
        parameterId: '0505',
        parameterName: 'Lippische L.Brandv.AG',
      },
      {
        parameterId: '0507',
        parameterName: 'Ostfriesische Lb',
      },
      {
        parameterId: '0522',
        parameterName: 'Öff.Feuer Sachs-Anh.',
      },
      {
        parameterId: '5008',
        parameterName: 'Sach- U. Haftpflicht',
      },
      {
        parameterId: '5015',
        parameterName: 'Nv-Versicherungen',
      },
      {
        parameterId: '5017',
        parameterName: 'Ostangler Brandgilde',
      },
      {
        parameterId: '5024',
        parameterName: 'Feuersozietät Berlin Brandenburg',
      },
      {
        parameterId: '5032',
        parameterName: 'Hamburger Feuerkasse',
      },
      {
        parameterId: '5033',
        parameterName: 'Freeyou Insurance AG',
      },
      {
        parameterId: '5036',
        parameterName: 'Sv Sparkassen-Vers. Gebäudevers. AG',
      },
      {
        parameterId: '5037',
        parameterName: 'Dbv-Winterthur Versicherung AG',
      },
      {
        parameterId: '5042',
        parameterName: 'Versicherungskammer Bayern',
      },
      {
        parameterId: '5044',
        parameterName: 'HDNA VvAG',
      },
      {
        parameterId: '5047',
        parameterName: 'CNA Insurance Comp.',
      },
      {
        parameterId: '5050',
        parameterName: 'Zürich Versicherung',
      },
      {
        parameterId: '5051',
        parameterName: 'Sparkassen Direkt Versicherung AG',
      },
      {
        parameterId: '5052',
        parameterName: 'Die Alternative Versicherungs AG',
      },
      {
        parameterId: '5055',
        parameterName: 'Verti Versicherung AG',
      },
      {
        parameterId: '5057',
        parameterName: 'Interlloyd Vers.-AG',
      },
      {
        parameterId: '5061',
        parameterName: 'Mannheimer Versicherung AG',
      },
      {
        parameterId: '5070',
        parameterName: 'Neckermann Vers. AG',
      },
      {
        parameterId: '5072',
        parameterName: 'If Schadenvers. AG',
      },
      {
        parameterId: '5078',
        parameterName: 'Janitos Versicherung AG',
      },
      {
        parameterId: '5080',
        parameterName: 'KRAVAG-Logistic Versicherungs-AG',
      },
      {
        parameterId: '5082',
        parameterName: 'Waldenburger Vers.AG',
      },
      {
        parameterId: '5083',
        parameterName: 'HvAG Hamburger Vers.',
      },
      {
        parameterId: '5085',
        parameterName: 'HDI Privat Versicherung AG',
      },
      {
        parameterId: '5086',
        parameterName: 'HUK24',
      },
      {
        parameterId: '5088',
        parameterName: 'Xl Insurance Comp.',
      },
      {
        parameterId: '5093',
        parameterName: 'Provinzial Versicherung AG Standort Münster',
      },
      {
        parameterId: '5095',
        parameterName: 'Provinzial Versicherung AG Standort Düsseldorf',
      },
      {
        parameterId: '5096',
        parameterName: 'Hdi Industrie',
      },
      {
        parameterId: '5098',
        parameterName: 'Versicherer Im Raum Der Kirchen',
      },
      {
        parameterId: '5207',
        parameterName: 'Neodigital Versicherung AG',
      },
      {
        parameterId: '5225',
        parameterName: 'Iptiq Emea P Und C S.A.',
      },
      {
        parameterId: '5232',
        parameterName: 'Lippische Landesbrandversicherung AG',
      },
      {
        parameterId: '5310',
        parameterName: 'Ba Die Bayerische Allgemeine Versicherung AG',
      },
      {
        parameterId: '5311',
        parameterName: 'DBV Beamten-Versicherung',
      },
      {
        parameterId: '5312',
        parameterName: 'Allianz AG',
      },
      {
        parameterId: '5316',
        parameterName: 'Badischer Gemeinde-Versicherungs-Verband',
      },
      {
        parameterId: '5317',
        parameterName: 'Barmenia Allgemeine',
      },
      {
        parameterId: '5318',
        parameterName: 'Basler Versicherung',
      },
      {
        parameterId: '5319',
        parameterName: 'Bayer. Hausbesitzer',
      },
      {
        parameterId: '5325',
        parameterName: 'Bayerische Versicherungsbank',
      },
      {
        parameterId: '5334',
        parameterName: 'Medien-Vers.',
      },
      {
        parameterId: '5338',
        parameterName: 'Concordia Versicherung',
      },
      {
        parameterId: '5339',
        parameterName: 'Condor Allgemeine',
      },
      {
        parameterId: '5340',
        parameterName: 'Continentale Sachversicherung',
      },
      {
        parameterId: '5342',
        parameterName: 'Generali Deutschland Versicherung AG',
      },
      {
        parameterId: '5343',
        parameterName: 'DA Deutsche Allgemeine',
      },
      {
        parameterId: '5344',
        parameterName: 'DEVK Sach Versicherung AG',
      },
      {
        parameterId: '5347',
        parameterName: 'Deutscher Herold Allgemeine',
      },
      {
        parameterId: '5350',
        parameterName: 'Deutscher Ring Sachvers.',
      },
      {
        parameterId: '5362',
        parameterName: 'Landesschadenhilfe Versicherungs-AG',
      },
      {
        parameterId: '5364',
        parameterName: 'Frankfurter AG',
      },
      {
        parameterId: '5365',
        parameterName: 'Gegenseitigkeit Versicherung Oldenburg',
      },
      {
        parameterId: '5366',
        parameterName: 'Gem.Haft.Gartenbau',
      },
      {
        parameterId: '5368',
        parameterName: 'Gerling-Konzern Allgemeine',
      },
      {
        parameterId: '5374',
        parameterName: 'Haftpflichtkasse Darmstadt',
      },
      {
        parameterId: '5375',
        parameterName: 'HUK-Coburg Beamten',
      },
      {
        parameterId: '5377',
        parameterName: 'Hdi Haftpflichtversicherung',
      },
      {
        parameterId: '5384',
        parameterName: 'Helvetia',
      },
      {
        parameterId: '5385',
        parameterName: 'Sparkassen Versicherung Allg.Vers. AG',
      },
      {
        parameterId: '5390',
        parameterName: 'Nova',
      },
      {
        parameterId: '5393',
        parameterName: 'Isselhorster',
      },
      {
        parameterId: '5397',
        parameterName: 'Asstel Sachversicherung AG',
      },
      {
        parameterId: '5400',
        parameterName: 'VGH - Landschaftliche Brandkasse Hannover',
      },
      {
        parameterId: '5401',
        parameterName: 'Itzehoer',
      },
      {
        parameterId: '5402',
        parameterName: 'LVM',
      },
      {
        parameterId: '5405',
        parameterName: 'Alte Leipziger',
      },
      {
        parameterId: '5408',
        parameterName: 'Royal International',
      },
      {
        parameterId: '5412',
        parameterName: 'Mecklenburgische Vers.-Gesellschaft A.G.',
      },
      {
        parameterId: '5414',
        parameterName: 'Münchner Verein Versicherungsgruppe',
      },
      {
        parameterId: '5420',
        parameterName: 'Hamburg-Mannheimer Sachversicherungs',
      },
      {
        parameterId: '5423',
        parameterName: 'Northern Assurance',
      },
      {
        parameterId: '5426',
        parameterName: 'Nürnberger Allgemeine',
      },
      {
        parameterId: '5432',
        parameterName: 'Patria Versicherung',
      },
      {
        parameterId: '5438',
        parameterName: 'R & V Allgemeine',
      },
      {
        parameterId: '5442',
        parameterName: 'Gerling G+A Vers.AG',
      },
      {
        parameterId: '5446',
        parameterName: 'Provinzial Brandkasse',
      },
      {
        parameterId: '5448',
        parameterName: 'Schweizer National',
      },
      {
        parameterId: '5451',
        parameterName: 'Signal Iduna Allg.Ver',
      },
      {
        parameterId: '5455',
        parameterName: 'ARAG Allgemeine',
      },
      {
        parameterId: '5456',
        parameterName: 'Generali Versicherung',
      },
      {
        parameterId: '5458',
        parameterName: 'Transatlant.Allg.Vers',
      },
      {
        parameterId: '5459',
        parameterName: 'Uelzener Allgemeine',
      },
      {
        parameterId: '5461',
        parameterName: 'VPV Allgemeine',
      },
      {
        parameterId: '5463',
        parameterName: 'Universa',
      },
      {
        parameterId: '5464',
        parameterName: 'VHV',
      },
      {
        parameterId: '5470',
        parameterName: 'Fahrlehrerversicherung',
      },
      {
        parameterId: '5472',
        parameterName: 'Ergo Versicherung AG',
      },
      {
        parameterId: '5473',
        parameterName: 'Generali Deutschland',
      },
      {
        parameterId: '5476',
        parameterName: 'WWK Allgemeine Versicherung AG',
      },
      {
        parameterId: '5479',
        parameterName: 'Württembergische Gemeinde-Versicherung AG',
      },
      {
        parameterId: '5480',
        parameterName: 'Württembergische U. Badische Vers. AG',
      },
      {
        parameterId: '5484',
        parameterName: 'Volkswohl-Bund',
      },
      {
        parameterId: '5485',
        parameterName: 'Grundeigentümer',
      },
      {
        parameterId: '5498',
        parameterName: 'ADAC Versicherung AG',
      },
      {
        parameterId: '5501',
        parameterName: 'Hanse-Merkur Allgemeine Versicherungs AG',
      },
      {
        parameterId: '5505',
        parameterName: 'Garanta Versicherungs-AG',
      },
      {
        parameterId: '5508',
        parameterName: 'Europa',
      },
      {
        parameterId: '5509',
        parameterName: 'Karlsruher Versicherung AG',
      },
      {
        parameterId: '5512',
        parameterName: 'Aspecta Vers. AG',
      },
      {
        parameterId: '5513',
        parameterName: 'DEVK Allgemeine Versicherung AG',
      },
      {
        parameterId: '5515',
        parameterName: 'AXA Versicherung AG',
      },
      {
        parameterId: '5516',
        parameterName: 'Familienschutz Vers.',
      },
      {
        parameterId: '5521',
        parameterName: 'HUK-Coburg-Allgemeine',
      },
      {
        parameterId: '5525',
        parameterName: 'WGV',
      },
      {
        parameterId: '5529',
        parameterName: 'D.A.S. Deutscher Automobil Schutz AG',
      },
      {
        parameterId: '5531',
        parameterName: 'Gothaer Allgemeine Vers',
      },
      {
        parameterId: '5546',
        parameterName: 'Inter Allg. Vers.',
      },
      {
        parameterId: '5549',
        parameterName: 'Debeka',
      },
      {
        parameterId: '5551',
        parameterName: 'Aioi Insurance Comp.',
      },
      {
        parameterId: '5552',
        parameterName: 'Cosmos',
      },
      {
        parameterId: '5562',
        parameterName: 'Karstadtquelle Vers. AG',
      },
      {
        parameterId: '5570',
        parameterName: 'Karlsruher Beamten',
      },
      {
        parameterId: '5581',
        parameterName: 'Adler / VödAG',
      },
      {
        parameterId: '5582',
        parameterName: 'Deut.Ärzte-Vers.All',
      },
      {
        parameterId: '5583',
        parameterName: 'PVAG Polizei-Versicherungs-AG',
      },
      {
        parameterId: '5585',
        parameterName: 'Gvv Direktversicherung AG',
      },
      {
        parameterId: '5586',
        parameterName: 'Stuttgarter Vers. AG',
      },
      {
        parameterId: '5590',
        parameterName: 'Würzburger Vers. AG',
      },
      {
        parameterId: '5592',
        parameterName: 'Lloyds Vers.',
      },
      {
        parameterId: '5593',
        parameterName: 'Badische Allgemeine',
      },
      {
        parameterId: '5595',
        parameterName: 'Aig Europe',
      },
      {
        parameterId: '5609',
        parameterName: 'Ancora',
      },
      {
        parameterId: '5632',
        parameterName: 'Delvag Luffahrtvers. AG',
      },
      {
        parameterId: '5633',
        parameterName: 'Baloise Sachversicherungs-AG',
      },
      {
        parameterId: '5636',
        parameterName: 'Elvia',
      },
      {
        parameterId: '5671',
        parameterName: 'Minerva Versicherung',
      },
      {
        parameterId: '5686',
        parameterName: 'Nürnberger Beamten',
      },
      {
        parameterId: '5754',
        parameterName: 'Hanse-Marine-Vers.AG',
      },
      {
        parameterId: '5771',
        parameterName: 'DARAG Deutschland AG',
      },
      {
        parameterId: '5773',
        parameterName: 'Saarland Versicherung',
      },
      {
        parameterId: '5781',
        parameterName: 'Sparkassen Versicherung Sachsen',
      },
      {
        parameterId: '5783',
        parameterName: 'Württembergische Versicherung',
      },
      {
        parameterId: '5787',
        parameterName: 'Bavaria Direkt Versicherung AG',
      },
      {
        parameterId: '5798',
        parameterName: 'Rheinland Versicherung AG',
      },
      {
        parameterId: '5858',
        parameterName: 'Gothaer Allgemeine Versicherung AG',
      },
      {
        parameterId: '7778',
        parameterName: 'Alpha Insurance A/S',
      },
      {
        parameterId: '8612',
        parameterName: 'Haftpflichtkasse Braunschweig',
      },
      {
        parameterId: '8909',
        parameterName: 'Gemeinnütz.Haft.Versicherung',
      },
      {
        parameterId: '9496',
        parameterName: 'Wefox Insurance AG',
      },
    ]);
  }

  /**
   * General Service
   */
  static postMultiQuote(): Observable<MultiQuoteResponse> {
    return of({
      regularPrices: [
        {
          kind: 'Regular',
          modifications: [],
          salesProduct: {
            paymentMethod: 'Debit',
            paymentFrequency: 'Yearly',
            prices: [
              {
                kind: 'Gross',
                paymentFrequency: 'Yearly',
                amount: '116.03',
                annualAmount: '116.03',
              },
            ],
            products: [
              {
                productType: 'PrivateLiability',
                insurancePeriod: {
                  begin: '2026-10-30',
                },
                conditions: [{}],
                prices: [
                  {
                    kind: 'Gross',
                    paymentFrequency: 'Yearly',
                    amount: '116.03',
                    annualAmount: '116.03',
                  },
                ],
                tariff: 'Family',
                elementaryProducts: [
                  {
                    productType: 'PrivateLiability',
                    prices: [
                      {
                        kind: 'Gross',
                        paymentFrequency: 'Yearly',
                        amount: '116.03',
                        annualAmount: '116.03',
                      },
                    ],
                    deductible: 'None',
                  },
                ],
                package: 'Classic',
              },
            ],
          },
        },
        {
          kind: 'Regular',
          modifications: [],
          salesProduct: {
            paymentMethod: 'Debit',
            paymentFrequency: 'Yearly',
            prices: [
              {
                kind: 'Gross',
                paymentFrequency: 'Yearly',
                amount: '142.80',
                annualAmount: '142.80',
              },
            ],
            products: [
              {
                productType: 'PrivateLiability',
                insurancePeriod: {
                  begin: '2026-10-30',
                },
                conditions: [{}],
                prices: [
                  {
                    kind: 'Gross',
                    paymentFrequency: 'Yearly',
                    amount: '142.80',
                    annualAmount: '142.80',
                  },
                ],
                tariff: 'Family',
                elementaryProducts: [
                  {
                    productType: 'PrivateLiability',
                    prices: [
                      {
                        kind: 'Gross',
                        paymentFrequency: 'Yearly',
                        amount: '142.80',
                        annualAmount: '142.80',
                      },
                    ],
                    deductible: 'None',
                  },
                ],
                package: 'Premium',
              },
            ],
          },
        },
      ],
    });
  }

  static getEmail(): Observable<EmailResponse> {
    return of({
      isBlacklisted: false,
    });
  }

  static getDomain(): Observable<EmailResponse> {
    return of({
      isBlacklisted: false,
    });
  }

  static getPrefix(): Observable<NumberResponse> {
    return of({
      isBlacklisted: false,
    });
  }

  static getNumber(): Observable<NumberResponse> {
    return of({
      isBlacklisted: false,
    });
  }

  static postVertiClient(): Observable<PartnerSearchResponse> {
    return of({
      isFound: true,
      badClient: false,
    });
  }

  static postDocument(): Observable<DocumentResponse> {
    const base64PdfContent =
      'JVBERi0xLjQKJcOcxoIKMSAwIG9iajw8L1BhZ2VzIDIgMCBSL1R5cGUvQ2F0YWxvZz4+CmVuZG9iagoyIDAgb2JqPDwvS2lkc1szIDAgUl0vQ291bnQgMS9UeXBlL1BhZ2VzPj4KZW5kb2JqCjMgMCBvYmo8PC9NZWRpYUJveFswIDAgNTk1IDg0Ml0vUGFyZW50IDIgMCBSL1R5cGUvUGFnZT4+CmVuZG9iagQ1NjAxIDAgaG9iagozIDAgUiA3MTMgMCBSCi8xLjMgZW5kb2JqCjIgMCBSIDcyNyAwIFIKL2NhdGFsb2cgb2JqCjExMjEgMCByCi9wYWdlcyBlbmRvYmoKNDEgMCByClggcmVmCiAgICAgICAgICAgICAgICAwMDAwMDAwMDE5IDAwMDAwIG4gCjAwMDAwMDAwMDAgMDAwMDAgbiAKdHJhaWxlcjw8L1Jvb3QgMSAwIFIvU2l6ZSA0L1N1YnN5c3RlbTEvRm9jYSBEb2MyMDcwPj4Kc3RhcnR4cmVmCjU3NDQKLy9FT0Y=';

    return of({
      documentContent: base64PdfContent,
    });
  }

  static postInitFlow(): any {
    return of({
      campaignInfo: {
        telephoneNumber: '030 - 890 003 004',
        initialImage: '',
        specialTariffImage: '',
        campaignCode: 'defaultCampaignCode',
        marketingId: '7583i011',
        cooperationId: '00',
      },
      promotionInfo: {},
      correlationId: '972c3ce9-f100-4c3d-8f21-3c9d653654b4',
      ratingDate: '2025-11-28',
      parameters: [
        {
          name: 'CO2Info',
          value: {
            offer: 'TEST2 CO2-Rabatt: 10% bereits abgezogen',
            speechBubble: {
              title: ' Dieses Fahrzeug erhält einen  CO2-Rabatt!',
              subtitle: '10% ERSPARNIS auf den Gesamtpreis.',
            },
            modal: {
              title: 'Herzlichen Glückwunsch',
              description:
                'Mit Ihrem Auto sind Sie umweltfreundlich unterwegs. Dafür belohnen wir Sie mit unserem exklusiven CO2-Rabatt!',
              bullets: [
                '10% ERSPARNIS auf den Gesamtpreis 10%*',
                'Bei Buchung des Schutzbriefs: Abschleppservice, wenn der Akku mal leer ist',
                'Absicherung des Ladekabels im Falle eines Diebstahls',
              ],
            },
          },
        },
        {
          name: 'CO2Offer',
          value: ' CO2-Rabatt: 10% bereits abgezogen',
        },
        {
          name: 'cover_details_plusbonus',
          value: {
            features: [
              {
                name: 'headline_liability',
                label: 'Haftpflicht-Leistungen im plusBonus-Tarif',
                list: [
                  {
                    name: 'Liability_includes_sign_1',
                    available: true,
                    label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                  },
                  {
                    name: 'Liability_includes_sign_2',
                    available: true,
                    label: 'Mallorca Police',
                  },
                  {
                    name: 'Liability_includes_sign_3',
                    available: true,
                    label: 'Eigenschadendeckung bei Sachschäden',
                  },
                  {
                    name: 'Liability_includes_sign_4',
                    available: true,
                    label: 'Update-Garantie ',
                  },
                ],
              },
              {
                name: 'headline_comprehensive',
                label: 'Teilkasko-Leistungen im plusBonus-Tarif',
                list: [
                  {
                    name: 'comprehensive_includes_sign_1',
                    available: true,
                    label: 'Schäden durch grobe Fahrlässigkeit',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    available: true,
                    label: 'Schäden durch Tiere aller Art',
                  },
                  {
                    name: 'comprehensive_includes_sign_3',
                    available: true,
                    label: 'Folgeschäden bei Tierbiss-Schäden unbegrenzt',
                  },
                  {
                    name: 'comprehensive_includes_sign_4',
                    available: true,
                    label: '50 Euro Gutschein für die Reduktion des Selbstbehalts',
                  },
                ],
              },
              {
                name: 'headline_full_comprehensive',
                label: 'Vollkasko-Leistungen im plusBonus-Tarif (Teilkasko ist in Vollkasko enthalten)',
                list: [
                  {
                    name: 'full_comprehensive_includes_sign_1',
                    available: true,
                    label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                  },
                ],
              },
            ],
            info: {
              coverageDescription: 'Kfz-Haftpflicht',
              description: 'This is a test',
            },
            productLine: {
              id: 'PlusBonus',
              value: 'PlusBonus',
            },
          },
        },
        {
          name: 'cover_details_classic',
          value: {
            info: {
              description: ' Der umfassende Rundum-Schutz zum günstigen Preis',
              coverageDescription: 'Kfz-Haftpflicht',
            },
            features: [
              {
                name: 'headline_liability',
                list: [
                  {
                    name: 'Liability_includes_sign_1',
                    available: true,
                    label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                  },
                  {
                    name: 'Liability_includes_sign_2',
                    available: true,
                    label: 'Mallorca Police',
                  },
                  {
                    name: 'Liability_includes_sign_3',
                    available: false,
                    label: 'Eigenschadendeckung bei Sachschäden',
                  },
                ],
                label: 'Haftpflicht-Leistungen im Klassik-Tarif',
              },
              {
                name: 'headline_comprehensive',
                list: [
                  {
                    name: 'comprehensive_includes_sign_1',
                    available: true,
                    label: 'Schäden durch grobe Fahrlässigkeit',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    available: true,
                    label: 'Schäden durch Tiere aller Art',
                  },
                  {
                    name: 'comprehensive_includes_sign_3',
                    available: true,
                    label: 'Folgeschäden bei Tierbiss-Schäden bis 5.500 Euro',
                  },
                ],
                label: 'Teilkasko-Leistungen im Klassik-Tarif',
              },
              {
                name: 'headline_full_comprehensive',
                list: [
                  {
                    name: 'full_comprehensive_includes_sign_1',
                    available: false,
                    label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                  },
                ],
                label: 'Vollkasko-Leistungen im Klassik-Tarif (Teilkasko ist in Vollkasko enthalten)',
              },
            ],
            productLine: {
              value: 'Klassik',
              id: 'Classic',
            },
          },
        },
        {
          name: 'tariffOptionDescriptions',
          value: {
            KaskoClever: {
              overlayDescription:
                ' Mit Kasko Clever überlassen Sie uns die Werkstattwahl und erhalten dadurch bessere Konditionen und einen Rabatt für Ihren Beitrag.',
              description: 'Dank Werkstattbindung von einem Rabatt und Zusatzleistungen profitieren',
              features: [
                'Bequeme Unfallabwicklung',
                'Kostenlose Abholung des Unfallautos',
                'Garantiert repariert',
                'Kostenloses Ersatzfahrzeug',
              ],
              percentageText: 'auf die Kaskodeckung',
              title: 'Kasko Clever',
              percentage: 'bis zu 15',
            },
            SCH: {
              title: 'Schutzbrief',
              overlayDescription:
                'Unser Schutzbrief sorgt dafür, dass Sie auch im Fall einer Panne immer Hilfe erhalten und das in ganz Europa.',
              description: 'Bei einer Panne sind wir für Sie da.',
            },
            FAS: {
              title: 'Fahrerschutz',
              overlayDescription:
                'Werden Sie bei einem selbst­verschul­deten Unfall verletzt, sind Sie in der Kfz-Ver­sicherung nicht versichert. Diese Lücke schließt der Verti Fahrerschutz und sorgt dafür, dass Sie finanziell geschützt sind. Wir zahlen pro Versicherungsfall bis zu 500.000 Euro und übernehmen Ihre Kosten für z. B. Verdienstausfall oder Pflegeleistungen.',
              description: 'Schützt Sie als Fahrer. Keine zusätzliche Kosten.',
            },
            NPT: {
              title: 'Nix-Passiert-Tarif',
              overlayDescription:
                'Mit dem Nix-Passiert-Tarif schützen Sie bei uns Ihre Schadenfreiheitsklasse, falls es einmal im Jahr zu einem Schaden kommt.',
              description: 'Schützt Ihren Schadenfreiheitsrabatt.',
            },
            VRS: {
              title: 'Verkehrsrechtsschutz',
              overlayDescription:
                'Mit unserem Verkehrsrechtsschutz sind Sie vor den finanziellen Folgen geschützt. Egal ob im Auto, auf dem Rad oder zu Fuß. Und das ganz ohne Wartezeiten.',
              description: 'Wir schützen Sie auch vor finanziellen Folgen.',
            },
            FKK: {
              title: 'Familienschutz "Kind &Kegel"',
              overlayDescription:
                'Schützen Sie sich und alle anderen Mitfahrer. Zusatzleistungen bei einem Krankenhausaufenthalt kommen besonders Kindern zugute. Auch Tierarztkosten für verunfallte Haustiere sind bis 1.000 Euro eingeschlossen. ',
              description: 'Schützen Sie sich und Ihre Mitfahrer.',
            },
          },
        },
        {
          name: 'cover_details_premium',
          value: {
            info: {
              description: ' Optimale Absicherung mit Premium-Schutz',
              coverageDescription: 'Kfz-Haftpflicht',
            },
            features: [
              {
                name: 'headline_liability',
                list: [
                  {
                    name: 'Liability_includes_sign_1',
                    available: true,
                    label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                  },
                  {
                    name: 'Liability_includes_sign_2',
                    available: true,
                    label: 'Mallorca Police',
                  },
                  {
                    name: 'Liability_includes_sign_3',
                    available: true,
                    label: 'Eigenschadendeckung bei Sachschäden',
                  },
                ],
                label: 'Haftpflicht-Leistungen im Premium-Tarif',
              },
              {
                name: 'headline_comprehensive',
                list: [
                  {
                    name: 'comprehensive_includes_sign_1',
                    available: true,
                    label: 'Schäden durch grobe Fahrlässigkeit',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    available: true,
                    label: 'Schäden durch Tiere aller Art',
                  },
                  {
                    name: 'comprehensive_includes_sign_3',
                    available: true,
                    label: 'Folgeschäden bei Tierbiss-Schäden bis 5.500 Euro',
                  },
                ],
                label: 'Teilkasko-Leistungen im Premium-Tarif',
              },
              {
                name: 'headline_full_comprehensive',
                list: [
                  {
                    name: 'full_comprehensive_includes_sign_1',
                    available: true,
                    label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                  },
                ],
                label: 'Vollkasko-Leistungen im Premium-Tarif (Teilkasko ist in Vollkasko enthalten)',
              },
            ],
            productLine: {
              value: 'Premium',
              id: 'Premium',
            },
          },
        },
        {
          name: 'cover_details_basis',
          value: {
            info: {
              description: ' Alle wichtigen Absicherungen in der Basis-Absicherung.',
              coverageDescription: 'Kfz-Haftpflicht',
            },
            features: [
              {
                name: 'headline_liability',
                list: [
                  {
                    name: 'Liability_includes_sign_1',
                    available: true,
                    label: 'Deckungssummen 100 Mio. pauschal, max. 15 Mio. pro Person',
                  },
                  {
                    name: 'Liability_includes_sign_2',
                    available: false,
                    label: 'Mallorca Police',
                  },
                  {
                    name: 'Liability_includes_sign_3',
                    available: false,
                    label: 'Eigenschadendeckung bei Sachschäden',
                  },
                ],
                label: 'Haftpflicht-Leistungen im Basis-Tarif',
              },
              {
                name: 'headline_comprehensive',
                list: [
                  {
                    name: 'comprehensive_includes_sign_1',
                    available: false,
                    label: 'Schäden durch grobe Fahrlässigkeit',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    available: true,
                    label: 'Schäden durch Tiere aller Art',
                  },
                  {
                    name: 'comprehensive_includes_sign_3',
                    available: false,
                    label: 'Folgeschäden bei Tierbiss-Schäden bis 5.500 Euro',
                  },
                ],
                label: 'Teilkasko-Leistungen im Basis-Tarif',
              },
              {
                name: 'headline_full_comprehensive',
                list: [
                  {
                    name: 'full_comprehensive_includes_sign_1',
                    available: false,
                    label: 'Leihwagen nach Unfall bzw. nach Fahrzeugentwendung',
                  },
                  {
                    name: 'comprehensive_includes_sign_2',
                    label: 'Die vollständige Leistungsübersicht finden Sie auf unseren Informationsseiten',
                  },
                ],
                label: 'Vollkasko-Leistungen im Basis-Tarif (Teilkasko ist in Vollkasko enthalten)',
              },
            ],
            productLine: {
              value: 'Basis',
              id: 'Basic',
            },
          },
        },
        {
          name: 'thank_you_mgm',
          value: {
            Graphic2: [
              'Schnell und einfach registrieren.',
              'Einen Freund als Neukunden zu Verti bringen',
              'Belohnung für jeden Neukunden kassieren.',
            ],
            Graphic1: {
              price: {
                amount: '50',
                currency: '€',
                text: 'Prämie erhalten',
                separator: ',',
                decimals: '00',
              },
            },
          },
        },
        {
          name: 'YEBVerti',
          value: {
            YEBPreSeasonStart: '15/09/2025',
            YEBPostSeasonEnd: '14/02/2026',
            YEBSeasonStart: '16/09/2025',
            YEBSeasonEnd: '15/02/2026',
          },
        },
      ],
      logicModules: [
        {
          logicModuleCode: 'new_vehicle',
          image: {
            name: 'plusBonus.png',
            url: 'https://d1nqe8q64bfnzv.cloudfront.net/logic-module-data/images/3f8c1d9e-7b2a-4d6b-9f8c-1e7b2a4d6b9f/plusBonus.png',
          },
          logicModuleId: '3f8c1d9e-7b2a-4d6b-9f8c-1e7b2a4d6b9f',
          activated: true,
        },
        {
          logicModuleCode: 'insurer_change',
          image: {
            name: 'tarifwechsel.png',
            url: 'https://d1nqe8q64bfnzv.cloudfront.net/logic-module-data/images/a1b2c3d4-e5f6-7a8b-9c0d-e1f2a3b4c5d6/tarifwechsel.png',
          },
          logicModuleId: 'a1b2c3d4-e5f6-7a8b-9c0d-e1f2a3b4c5d6',
          activated: true,
        },
      ],
    });
  }

  static postGetOrder(): any {
    return of({
      transactionNumber: 'NOVUM_GETORDER_123456789',
      salesProduct: {
        paymentMethod: 'Debit',
        paymentFrequency: 'Yearly',
        prices: [
          {
            paymentFrequency: 'Yearly',
            kind: 'Gross',
            amount: '171,40',
          },
        ],
        products: [
          {
            productType: 'PrivateLiability',
            tariff: 'Single',
            package: 'Premium',
            insurancePeriod: {
              begin: '2025-08-01',
            },
            prices: [
              {
                paymentFrequency: 'Yearly',
                kind: 'Gross',
                amount: '171,40',
              },
            ],
            elementaryProducts: [
              {
                productType: 'PrivateLiability',
                deductible: '500',
                prices: [
                  {
                    paymentFrequency: 'Yearly',
                    kind: 'Gross',
                    amount: '171,40',
                  },
                ],
              },
            ],
          },
        ],
      },
      documents: [
        {
          documentKind: 'AllInOne',
          documentContent:
            'JVBERi0xLjQKJcOcxoIKMSAwIG9iajw8L1BhZ2VzIDIgMCBSL1R5cGUvQ2F0YWxvZz4+CmVuZG9iagoyIDAgb2JqPDwvS2lkc1szIDAgUl0vQ291bnQgMS9UeXBlL1BhZ2VzPj4KZW5kb2JqCjMgMCBvYmo8PC9NZWRpYUJveFswIDAgNTk1IDg0Ml0vUGFyZW50IDIgMCBSL1R5cGUvUGFnZT4+CmVuZG9iagQ1NjAxIDAgaG9iagozIDAgUiA3MTMgMCBSCi8xLjMgZW5kb2JqCjIgMCBSIDcyNyAwIFIKL2NhdGFsb2cgb2JqCjExMjEgMCByCi9wYWdlcyBlbmRvYmoKNDEgMCByClggcmVmCiAgICAgICAgICAgICAgICAwMDAwMDAwMDE5IDAwMDAwIG4gCjAwMDAwMDAwMDAgMDAwMDAgbiAKdHJhaWxlcjw8L1Jvb3QgMSAwIFIvU2l6ZSA0L1N1YnN5c3RlbTEvRm9jYSBEb2MyMDcwPj4Kc3RhcnR4cmVmCjU3NDQKLy9FT0Y=',
        },
      ],
    });
  }

  static postOrder(): any {
    return of({
      salesProduct: {
        paymentMethod: 'Debit',
        paymentFrequency: 'Yearly',
        prices: [
          {
            kind: 'Gross',
            paymentFrequency: 'Yearly',
            amount: '87.02',
            annualAmount: '87.02',
          },
        ],
        products: [
          {
            productType: 'PrivateLiability',
            insurancePeriod: {
              begin: '2026-12-30',
            },
            prices: [
              {
                kind: 'Gross',
                paymentFrequency: 'Yearly',
                amount: '87.02',
                annualAmount: '87.02',
              },
            ],
            tariff: 'Family',
            elementaryProducts: [
              {
                productType: 'PrivateLiability',
                prices: [
                  {
                    kind: 'Gross',
                    paymentFrequency: 'Yearly',
                    amount: '87.02',
                    annualAmount: '87.02',
                  },
                ],
                deductible: '300',
              },
            ],
            package: 'Classic',
          },
        ],
      },
      contractNumber: 'PSV300000410',
      additionalInformation: [
        {
          infoCode: 'PARTNER_NEWLY_CREATED',
          systemMsg: "partner with partnerId 'PAR01' was newly created (200000843)",
          userMsg: 'Der Partner wurde im Partner System neu angelegt.',
        },
      ],
    });
  }

  static postPartnerSearch(): any {
    return of({
      value: 'NOT_IMPLEMENTED',
      details: ['2051', '2052', '2053'],
    });
  }

  static postClient(): any {
    return of({
      isBlacklisted: false,
      value: [
        {
          risktype: 'string',
          rescode: 'string',
        },
      ],
    });
  }
}

export enum PreviousInsuranceDurationEnum {
  UNKNOWN = 'Unknown',
  UP_TO_ONE_YEAR = 'UpToOneYear',
  LONGER_THAN_ONE_YEAR = 'LongerThanOneYear',
  LONGER_THAN_THREE_YEAR = 'LongerThanThreeYear',
  LONGER_THAN_FIVE_YEAR = 'LongerThanFiveYear',
}
